import API_BASE_URL from "./config";

/**
 * Patches window.fetch so every request to this app's own backend (relative
 * /api/v1/... paths, or an absolute API_BASE_URL URL) always sends the session
 * cookie, and broadcasts a session-expiry event on 401.
 *
 * Why a global patch instead of migrating the ~29 existing fetch() call
 * sites individually: the frontend and backend are different origins in
 * local dev (only same-origin in the Traefik-routed production deployment),
 * so cookies are dropped on cross-origin fetches unless credentials:"include"
 * is set. Patching fetch once here is the same fix with far less blast
 * radius than touching every call site, and — unlike a manual sweep —
 * guarantees none is missed.
 */
let installed = false;

function isOwnApiUrl(input) {
  const url = typeof input === "string" ? input : input?.url || "";
  return url.startsWith("/api") || url.startsWith(API_BASE_URL);
}

export function installApiClient() {
  if (installed) return;
  installed = true;
  const originalFetch = window.fetch.bind(window);

  window.fetch = async (input, init = {}) => {
    if (!isOwnApiUrl(input)) {
      return originalFetch(input, init);
    }
    const response = await originalFetch(input, {
      ...init,
      credentials: init.credentials || "include",
    });
    if (response.status === 401) {
      window.dispatchEvent(new CustomEvent("auth:unauthorized"));
    }
    return response;
  };
}

/** Thin JSON helper — reads `.detail` from the backend's error envelope consistently.
 * Caller headers are merged over the JSON content type (never replace it); the parsed
 * error body is kept on `error.body` for structured (422/409) details. */
export async function apiFetchJson(path, options = {}) {
  const { headers, ...rest } = options;
  const response = await fetch(`${API_BASE_URL}${path}`, {
    ...rest,
    headers: { "Content-Type": "application/json", ...(headers || {}) },
  });
  let body = null;
  try {
    body = await response.json();
  } catch {
    body = null;
  }
  if (!response.ok) {
    const detail = body && body.detail;
    const message = typeof detail === "string" ? detail
      : (detail && detail.message) || `Request failed (${response.status})`;
    const error = new Error(message);
    error.status = response.status;   // lets a caller tell "gone" (404) from "failed"
    error.body = body;
    throw error;
  }
  return body;
}

/** Download a file from the backend: errors read `.detail` like apiFetchJson; the file name comes
 * from Content-Disposition (else `fallbackName`). Returns the saved file name. */
export async function apiDownload(path, fallbackName) {
  const response = await fetch(`${API_BASE_URL}${path}`);
  if (!response.ok) {
    let detail = null;
    try {
      detail = (await response.json()).detail;
    } catch {
      detail = null;
    }
    const error = new Error(typeof detail === "string" ? detail : `Request failed (${response.status})`);
    error.status = response.status;
    throw error;
  }
  const disposition = response.headers.get("content-disposition") || "";
  const match = /filename="?([^";]+)"?/i.exec(disposition);
  const name = (match && match[1]) || fallbackName;
  const url = URL.createObjectURL(await response.blob());
  const link = document.createElement("a");
  link.href = url;
  link.download = name;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
  return name;
}

/** A fresh Idempotency-Key for one user action (a retried click reuses the same key). */
export function newIdempotencyKey() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
}

/** Every item of a cursor-paginated list endpoint ({items, next_cursor}): follows
 * `next_cursor` until the server returns null. A cursor that repeats would loop forever,
 * so it is treated as a server fault. Pass `path` without a cursor parameter. */
export async function fetchAllPages(path) {
  const items = [];
  const seen = new Set();
  let cursor = null;
  do {
    const separator = path.includes("?") ? "&" : "?";
    const page = await apiFetchJson(cursor ? `${path}${separator}cursor=${encodeURIComponent(cursor)}` : path);
    items.push(...((page && page.items) || []));
    cursor = (page && page.next_cursor) || null;
    if (cursor !== null) {
      if (seen.has(cursor)) throw new Error("Pagination returned a repeated cursor");
      seen.add(cursor);
    }
  } while (cursor !== null);
  return items;
}
